import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import {
  ClinicSubscriptionSummaryDto, PaymentMethodDto, StartPaymentMethodUpdateResultDto,
} from '../../dtos/organization-subscription.dto';
import { OrganizationSubscriptionRemoteDatasource } from '../remote/organization-subscription.remote-datasource';

// The saved card is the one thing on the Facturación screen that has no database behind it: the
// backend reads it live from Stripe, and a host with no gateway credentials answers 503 on every
// payment-method endpoint. Worse, the panel does not even get that far -- `CanManagePaymentMethod`
// is false for a sandbox organization or one with no Stripe customer, so the card branch is
// unreachable and only the "no disponible" state ever renders.
//
// This subclass forges exactly that surface (and nothing else: every other call still goes to the
// real API through `super`) so the card, empty and busy states can be styled locally. It is wired
// in by `environment.useClinicPaymentMethodFake`, true ONLY in environment.local.ts -- same idiom
// as the mobile app's `useAppApiFakes` datasources.
const FAKE_DELAY_MS = 400;

const FAKE_CARD: PaymentMethodDto = {
  Type: 'card', Brand: 'Visa', Last4: '4242', ExpMonth: 11, ExpYear: 2029,
};

const FAKE_REPLACEMENT_CARD: PaymentMethodDto = {
  Type: 'card', Brand: 'Mastercard', Last4: '5454', ExpMonth: 3, ExpYear: 2031,
};

@Injectable()
export class OrganizationSubscriptionPaymentMethodFakeDatasource extends OrganizationSubscriptionRemoteDatasource {
  // Mutable, unlike the mobile read-only fakes: removing the card has to leave the panel in its
  // empty state so that branch is reachable too (reload restores the card).
  private card: PaymentMethodDto | null = FAKE_CARD;

  public override get(organizationId: number): Observable<ClinicSubscriptionSummaryDto> {
    return super.get(organizationId).pipe(
      map((dto) => (dto.HasSubscription ? { ...dto, CanManagePaymentMethod: true } : dto)),
    );
  }

  public override getPaymentMethod(): Observable<PaymentMethodDto | null> {
    return of(this.card).pipe(delay(FAKE_DELAY_MS));
  }

  // Enough to open the drawer. Stripe.js still refuses these ids, so the drawer lands on its
  // "el formulario no se pudo cargar" branch -- that form cannot be faked from here, it is mounted
  // by Stripe itself.
  public override startPaymentMethodUpdate(): Observable<StartPaymentMethodUpdateResultDto> {
    return of<StartPaymentMethodUpdateResultDto>({
      ClientSecret: 'seti_fake_secret',
      PublishableKey: 'pk_test_fake',
      SetupIntentId: 'seti_fake',
    }).pipe(delay(FAKE_DELAY_MS));
  }

  public override confirmPaymentMethodUpdate(): Observable<PaymentMethodDto> {
    return of(FAKE_REPLACEMENT_CARD).pipe(
      delay(FAKE_DELAY_MS),
      tap((card) => { this.card = card; }),
    );
  }

  public override removePaymentMethod(): Observable<void> {
    return of<void>(undefined).pipe(
      delay(FAKE_DELAY_MS),
      tap(() => { this.card = null; }),
    );
  }
}
